import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { retentionFor } from '@content-engine/shared';
import {
  archiveEntity,
  fetchBanner,
  fetchVariant,
  regenerateVariantApi,
  updateVariantApi,
} from '../../lib/queries.ts';
import type { ClipVariant } from '../../lib/types.ts';
import { ArchiveDialog } from '../../components/archive/index.ts';

interface VariantLightboxProps {
  variants: ClipVariant[];
  currentIndex: number;
  onClose: () => void;
  onChange: () => void;
  onIndexChange: (index: number) => void;
}

export function VariantLightbox({
  variants,
  currentIndex,
  onClose,
  onChange,
  onIndexChange,
}: VariantLightboxProps): React.ReactElement | null {
  const { t } = useTranslation();
  const variant = variants[currentIndex];
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  useEffect(() => {
    // Пока открыт диалог архивации, клавиши принадлежат ему (Escape закрывает
    // диалог, а не лайтбокс под ним).
    if (showArchiveDialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && currentIndex > 0) onIndexChange(currentIndex - 1);
      else if (e.key === 'ArrowRight' && currentIndex < variants.length - 1) onIndexChange(currentIndex + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentIndex, variants.length, onClose, onIndexChange, showArchiveDialog]);

  const detailQuery = useQuery({
    queryKey: ['clipVariant', variant?.id],
    queryFn: () => fetchVariant(variant!.id),
    enabled: !!variant && (variant.status === 'ready' || variant.status === 'approved' || variant.status === 'rejected'),
  });

  const bannerQuery = useQuery({
    queryKey: ['banner', variant?.bannerLibraryId],
    queryFn: () => fetchBanner(variant!.bannerLibraryId!),
    enabled: !!variant && !!variant.bannerLibraryId,
  });

  const regenerateMutation = useMutation({
    mutationFn: () => regenerateVariantApi(variant!.id),
    onSuccess: onChange,
  });
  // Вариант самодостаточен: его архивация не трогает ни клип, ни фильм.
  const archiveMutation = useMutation({
    mutationFn: (opts: { freeFiles: boolean }) => archiveEntity('clip_variant', variant!.id, opts),
    onSuccess: () => {
      setShowArchiveDialog(false);
      onChange();
      onClose();
    },
  });

  if (!variant) return null;

  const url = detailQuery.data?.presignedUrl;
  const banner = bannerQuery.data?.banner;
  const canModerate = variant.status === 'ready' || variant.status === 'approved' || variant.status === 'rejected';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          gap: 24,
          maxWidth: '95vw',
          maxHeight: '95vh',
          alignItems: 'stretch',
        }}
      >
        {/* Video */}
        <div
          style={{
            position: 'relative',
            background: '#000',
            borderRadius: 8,
            overflow: 'hidden',
            aspectRatio: '9 / 16',
            height: '85vh',
            maxWidth: '50vw',
          }}
        >
          {url ? (
            <video src={url} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
              {variant.status === 'pending' || variant.status === 'rendering' ? (
                <span className="working"><span className="spinner" /> {t(`clips.variantStatus_${variant.status}`)}</span>
              ) : (
                t(`clips.variantStatus_${variant.status}`)
              )}
            </div>
          )}
        </div>

        {/* Info panel */}
        <div
          style={{
            background: 'var(--bg-card)',
            borderRadius: 8,
            padding: 20,
            width: 320,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {t('clips.variantLightboxTitle', { index: currentIndex + 1, total: variants.length })}
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>
                {banner?.name ?? '…'}
              </div>
            </div>
            <button onClick={onClose} style={{ fontSize: 14, padding: '4px 10px' }} title={t('clips.variantLightboxClose')}>
              ✕
            </button>
          </div>

          <span className={`pill ${variant.status === 'approved' ? 'live' : ''}`}>
            {t(`clips.variantStatus_${variant.status}`)}
          </span>

          <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
            <div><strong>{t('clips.variantPositionLabel')}:</strong> {variant.position}</div>
            <div><strong>{t('clips.variantSizeLabel')}:</strong> {variant.sizePct}%</div>
            {variant.startSec && (
              <div><strong>{t('clips.variantTimingLabel')}:</strong> {variant.startSec}s – {variant.endSec ?? '∞'}s</div>
            )}
            {variant.chromaKeyColor && (
              <div><strong>{t('clips.variantChromaLabel')}:</strong> {variant.chromaKeyColor}</div>
            )}
          </div>

          {variant.renderError && (
            <div style={{ fontSize: 11, color: 'var(--hot)', padding: 8, background: 'rgba(255,0,0,0.05)', borderRadius: 4 }}>
              {variant.renderError}
            </div>
          )}

          {variant.rejectedReason && (
            <div style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>
              {variant.rejectedReason}
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
            {canModerate && (
              <>
                <button onClick={() => void updateVariantApi(variant.id, { status: 'approved' }).then(onChange)}>
                  ✓ {t('clips.approve')}
                </button>
                <button
                  onClick={() => {
                    const reason = prompt(t('clips.rejectReasonPrompt')) ?? '';
                    void updateVariantApi(variant.id, { status: 'rejected', rejectedReason: reason }).then(onChange);
                  }}
                >
                  ✗ {t('clips.reject')}
                </button>
              </>
            )}
            <button onClick={() => regenerateMutation.mutate()} disabled={regenerateMutation.isPending}>
              ↻ {t('clips.regenerate')}
            </button>
            {url && (
              <a
                href={url}
                download={`clip-${variant.clipId}-variant-${variant.id}.mp4`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 10px',
                  fontSize: 12,
                  border: '1px solid var(--line-strong)',
                  borderRadius: 6,
                  color: 'var(--text-2)',
                  textDecoration: 'none',
                }}
              >
                ↓ {t('clips.download')}
              </a>
            )}
            <button type="button" onClick={() => setShowArchiveDialog(true)}>
              {t('clips.detail.archive')}
            </button>
          </div>
        </div>
      </div>

      {/* Клик по подложке диалога не должен проваливаться в лайтбокс под ним. */}
      {showArchiveDialog && (
        <div onClick={(e) => e.stopPropagation()}>
          <ArchiveDialog
            kind="variant"
            freeAfterDays={retentionFor('clip_variant')}
            sizeBytes={detailQuery.data?.file.sizeBytes ?? variant.sizeBytes}
            busy={archiveMutation.isPending}
            onCancel={() => setShowArchiveDialog(false)}
            onConfirm={(opts) => archiveMutation.mutate(opts)}
          />
        </div>
      )}

      {/* Nav arrows */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onIndexChange(currentIndex - 1); }}
          style={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            width: 44,
            height: 44,
            borderRadius: '50%',
            fontSize: 20,
            cursor: 'pointer',
          }}
          title={t('clips.variantLightboxPrev')}
        >
          ‹
        </button>
      )}
      {currentIndex < variants.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onIndexChange(currentIndex + 1); }}
          style={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            width: 44,
            height: 44,
            borderRadius: '50%',
            fontSize: 20,
            cursor: 'pointer',
          }}
          title={t('clips.variantLightboxNext')}
        >
          ›
        </button>
      )}
    </div>
  );
}
